import React, { useState, useRef, useEffect } from 'react';
import { MapPin, X, ToggleLeft, ToggleRight } from 'lucide-react';
import { Spinner } from 'flowbite-react';
import Autosuggest from 'react-autosuggest';
import { useSettings } from '../../../contexts/SettingsContext';
import { getLocationName, searchLocations } from '../../../services/locationService';
import { useLocation } from '../../../contexts/LocationContext';
import { useDarkMode } from '../../../contexts/DarkModeContext';

function extractPlaceName(suggestion) {
  if (!suggestion) return '';
  if (suggestion.address) {
    return (
      suggestion.address.city ||
      suggestion.address.town ||
      suggestion.address.village ||
      suggestion.address.suburb ||
      (suggestion.display_name ? suggestion.display_name.split(',')[0].trim() : '')
    );
  }
  if (suggestion.display_name) {
    return suggestion.display_name.split(',')[0].trim();
  }
  return '';
}

export const AdhanLocationSettings = ({ onSave }) => {
  const { isDarkMode } = useDarkMode();
  const { settings, updateSettings } = useSettings();
  const { getLocation } = useLocation();
  
  const [location, setLocation] = useState(settings.adhanClockLocation?.location || '');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [isEnabled, setIsEnabled] = useState(settings.adhanClockLocation?.enabled || false);

  const handleToggle = () => {
    const newEnabled = !isEnabled;
    setIsEnabled(newEnabled);
    
    const updatedSettings = {
      adhanClockLocation: {
        ...settings.adhanClockLocation,
        enabled: newEnabled
      }
    };
    
    updateSettings(updatedSettings);
    if (onSave) onSave(updatedSettings);
  };

  const handleLocationChange = (event, { newValue }) => {
    setLocation(newValue);
  };

  const handleDeleteLocation = () => {
    const updatedSettings = {
      adhanClockLocation: {
        ...settings.adhanClockLocation,
        location: null,
        coordinates: null
      }
    };
    
    updateSettings(updatedSettings);
    if (onSave) onSave(updatedSettings);
    setLocation('');
  };

  const handleAutoDetectLocation = async () => {
    setIsLoading(true);
    try {
      const coords = await getLocation();
      const { latitude, longitude } = coords;
      
      try {
        const locationName = await getLocationName(latitude, longitude);
        
        const updatedSettings = {
          adhanClockLocation: {
            ...settings.adhanClockLocation,
            location: locationName,
            coordinates: {
              lat: latitude,
              lon: longitude
            }
          }
        };
        
        setLocation(locationName);
        updateSettings(updatedSettings);
        if (onSave) onSave(updatedSettings);
      } catch (error) {
        console.error("Error getting location name:", error);
      }
    } catch (error) {
      console.error("Error detecting location:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const debounceTimeoutRef = useRef(null);

  const onSuggestionsFetchRequested = ({ value }) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await searchLocations(value);
        setSuggestions(results);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      }
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    };
  }, []);

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const onSuggestionSelected = async (event, { suggestion }) => {
    const placeName = extractPlaceName(suggestion);
    setLocation(placeName);
    
    const updatedSettings = {
      adhanClockLocation: {
        ...settings.adhanClockLocation,
        location: placeName,
        coordinates: {
          lat: suggestion.lat,
          lon: suggestion.lon
        }
      }
    };
    
    updateSettings(updatedSettings);
    if (onSave) onSave(updatedSettings);
  };

  const getSuggestionValue = suggestion => extractPlaceName(suggestion);

  const renderSuggestion = (suggestion) => (
    <div className="text-sm py-2 px-4 hover:bg-gray-100 cursor-pointer">
      {suggestion.display_name}
    </div>
  );

  const inputProps = {
    placeholder: 'Enter location (city or street)',
    value: location,
    onChange: handleLocationChange,
    disabled: !isEnabled,
    className: `w-full h-[42px] px-4 rounded-xl border ${
      isDarkMode 
        ? 'bg-[#1A1A1A] border-gray-800 text-white' 
        : 'bg-white border-gray-200 text-gray-900'
    } ${!isEnabled ? 'opacity-50' : ''} focus:ring-emerald-500 focus:border-emerald-500 outline-none`,
  };

  return (
    <div className={`rounded-2xl border p-6 ${
      isDarkMode 
        ? 'bg-[#111111] border-gray-800' 
        : 'bg-white border-gray-200'
    }`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <MapPin className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Adhan Clock Location
          </h3>
        </div>
        <button
          onClick={handleToggle}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
            isEnabled 
              ? (isDarkMode ? 'bg-emerald-600/20 text-emerald-400' : 'bg-emerald-50 text-emerald-600')
              : (isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600')
          }`}
        >
          {isEnabled ? (
            <ToggleRight className="w-5 h-5" />
          ) : (
            <ToggleLeft className="w-5 h-5" />
          )}
          <span className="text-sm">{isEnabled ? 'Enabled' : 'Disabled'}</span>
        </button>
      </div>

      <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        Use a different location for the adhan clock instead of the main location setting.
      </p>

      {isEnabled && (
        <div>
          <label className={`block text-sm mb-2 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>Location</label>
          <Autosuggest
            suggestions={suggestions}
            onSuggestionsFetchRequested={onSuggestionsFetchRequested}
            onSuggestionsClearRequested={onSuggestionsClearRequested}
            onSuggestionSelected={onSuggestionSelected}
            getSuggestionValue={getSuggestionValue}
            renderSuggestion={renderSuggestion}
            inputProps={inputProps}
            theme={{
              container: 'relative flex-grow',
              suggestionsContainer: `absolute z-10 w-full mt-1 rounded-xl ${
                isDarkMode ? 'bg-[#1A1A1A]' : 'bg-white'
              }`,
              suggestionsContainerOpen: `border shadow-lg ${
                isDarkMode ? 'border-gray-800' : 'border-gray-200'
              }`,
              suggestionsList: 'list-none p-0 m-0 max-h-60 overflow-auto rounded-xl',
              suggestion: `p-3 cursor-pointer ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`,
              suggestionHighlighted: isDarkMode ? 'bg-gray-800' : 'bg-gray-50',
            }}
          />
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleAutoDetectLocation}
              disabled={isLoading}
              className={`flex items-center justify-center gap-2 ${
                isDarkMode 
                  ? 'bg-emerald-600/20 text-emerald-400' 
                  : 'bg-emerald-600 text-white'
              } px-4 py-2 rounded-xl flex-1`}
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" />
                  <span>Detecting...</span>
                </>
              ) : (
                <>
                  <MapPin className="w-4 h-4" />
                  <span className="text-sm">Detect</span>
                </>
              )}
            </button>
            <button
              onClick={handleDeleteLocation}
              disabled={!settings.adhanClockLocation?.location}
              className={`flex items-center justify-center gap-2 ${
                isDarkMode 
                  ? 'bg-red-600/20 text-red-400' 
                  : 'bg-red-600 text-white'
              } px-4 py-2 rounded-xl flex-1`}
            >
              <X className="w-4 h-4" />
              <span className="text-sm">Remove</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};